import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { describeError } from "@/api/client";
import { useCanEdit } from "@/api/session";
import {
  AlertDialog,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogClose,
  EmptyState,
  Money,
  PageHeader,
  RelativeTime,
  TextField,
  Toolbar,
  useToast,
  type DataTableColumn,
  type RowAction,
} from "@/components";
import { formatDate, pluralize } from "@/lib/format";
import { useNow, useSearchParamsInPlace } from "@/lib/hooks";
import { useBaskets, useCreateBasket, useDeleteBasket, useSetBasketStatus, type Basket } from "../api";
import { BasketStatusBadge } from "../components/BasketStatusBadge";
import { ShoppingListBuilder } from "../components/ShoppingListBuilder";
import styles from "./PurchasingPage.module.css";

/** Purchasing: can I make this, shopping lists from formulas, and the baskets that come out of them. */
export function PurchasingPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const showArchived = searchParams.get("archived") === "1";
  const baskets = useBaskets({ include_archived: showArchived, limit: 100 });
  const canEdit = useCanEdit();
  const [newOpen, setNewOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Basket | null>(null);
  const remove = useDeleteBasket();
  const setStatus = useSetBasketStatus();
  const toast = useToast();

  function archive(basket: Basket, archived: boolean) {
    setStatus.mutate(
      { id: basket.id, status: archived ? "archived" : "draft" },
      {
        onSuccess: () =>
          toast.success(archived ? `Basket “${basket.name}” archived` : `Basket “${basket.name}” restored`, {
            action: archived
              ? { label: "Undo", onClick: () => setStatus.mutate({ id: basket.id, status: basket.status }) }
              : undefined,
          }),
        onError: (error) => toast.error("Couldn’t update the basket", { description: describeError(error) }),
      },
    );
  }

  const columns: DataTableColumn<Basket>[] = [
    {
      id: "name",
      header: "Basket",
      width: "wide",
      sortable: true,
      sortValue: (basket) => basket.name.toLowerCase(),
      cell: (basket) => (
        <Link to={`/purchasing/baskets/${basket.id}`} className={styles.name}>
          {basket.name}
        </Link>
      ),
    },
    { id: "status", header: "Status", width: "fit", cell: (basket) => <BasketStatusBadge status={basket.status} /> },
    {
      id: "lines",
      header: "Lines",
      numeric: true,
      sortable: true,
      sortValue: (basket) => basket.line_count,
      cell: (basket) => basket.line_count,
    },
    {
      id: "plan",
      header: "Latest plan",
      numeric: true,
      sortable: true,
      sortValue: (basket) => basket.latest_plan?.total_minor ?? null,
      cell: (basket) =>
        basket.latest_plan ? (
          <span className={styles.plan}>
            <Money minor={basket.latest_plan.total_minor} currency={basket.latest_plan.currency} />
            <span className={styles.subtle}>{pluralize(basket.latest_plan.parcels, "parcel", "parcels")}</span>
          </span>
        ) : (
          <span className={styles.subtle}>Not planned</span>
        ),
    },
    {
      id: "created",
      header: "Created",
      sortable: true,
      sortValue: (basket) => basket.created_at,
      cell: (basket) => <RelativeTime value={basket.created_at} />,
    },
  ];

  const rowActions = (basket: Basket): RowAction<Basket>[] => [
    {
      label: basket.status === "archived" ? "Restore" : "Archive",
      icon: "box",
      disabled: !canEdit,
      onSelect: (row) => archive(row, row.status !== "archived"),
    },
    { label: "Delete…", icon: "trash", danger: true, disabled: !canEdit, onSelect: setToDelete },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Purchasing"
        description="What to buy for the formulas you plan to make, split across suppliers at the lowest landed cost."
        actions={
          <Button icon="plus" onClick={() => setNewOpen(true)} disabled={!canEdit}>
            New basket…
          </Button>
        }
      />
      <ShoppingListBuilder />
      <section className={styles.section} aria-labelledby="baskets-title">
        <Toolbar
          end={
            <Checkbox
              label="Show archived"
              checked={showArchived}
              onCheckedChange={(checked) =>
                setSearchParams(
                  (current) => {
                    const next = new URLSearchParams(current);
                    if (checked) next.set("archived", "1");
                    else next.delete("archived");
                    return next;
                  },
                  { replace: true },
                )
              }
            />
          }
        >
          <h2 id="baskets-title" className={styles.sectionTitle}>
            Baskets
          </h2>
        </Toolbar>
        <DataTable
          caption="Baskets"
          columns={columns}
          rows={baskets.data?.items}
          getRowId={(basket) => basket.id}
          loading={baskets.isPending}
          error={baskets.error}
          onRetry={() => void baskets.refetch()}
          rowActions={rowActions}
          rowActionsLabel={(basket) => `Actions for ${basket.name}`}
          empty={
            <EmptyState
              variant="inline"
              headingLevel={3}
              icon="cart"
              title="No baskets yet"
              description="Create one from a formula’s shortfall above, or start an empty basket and add materials."
            />
          }
        />
      </section>
      <NewBasketDialog open={newOpen} onOpenChange={setNewOpen} />
      <AlertDialog
        open={toDelete !== null}
        onOpenChange={(open) => (open ? undefined : setToDelete(null))}
        title={`Delete basket “${toDelete?.name ?? ""}”?`}
        description="Its lines and plans are deleted too. Archive it instead to keep the history."
        confirmLabel={`Delete ${toDelete?.name ?? "basket"}`}
        pending={remove.isPending}
        onConfirm={() => {
          if (!toDelete) return;
          remove.mutate(toDelete.id, {
            onSuccess: () => {
              toast.success(`Basket “${toDelete.name}” deleted`);
              setToDelete(null);
            },
            onError: (error) => toast.error("Couldn’t delete the basket", { description: describeError(error) }),
          });
        }}
      />
    </div>
  );
}

function NewBasketDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [name, setName] = useState("");
  const create = useCreateBasket();
  const navigate = useNavigate();
  const toast = useToast();
  const now = useNow(60_000);
  const fallback = `Basket ${formatDate(now)}`;

  function submit() {
    create.mutate(
      { name: name.trim() || fallback, lines: [] },
      {
        onSuccess: (basket) => {
          toast.success(`Basket “${basket.name}” created`);
          onOpenChange(false);
          setName("");
          void navigate(`/purchasing/baskets/${basket.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New basket"
      size="sm"
      footer={
        <>
          <DialogClose />
          <Button variant="primary" onClick={submit} pending={create.isPending} pendingLabel="Creating…">
            Create basket
          </Button>
        </>
      }
    >
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField label="Name" placeholder={fallback} value={name} onValueChange={setName} autoComplete="off" />
        {create.isError ? <p className={styles.error}>{describeError(create.error)}</p> : null}
      </form>
    </Dialog>
  );
}
